import { useId, useState } from "react";
import type { FormEvent } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError, ROLES, toApiError, unwrap, type MemberOut, type Role } from "../api/client";
import { useAuth, useMe } from "../auth/AuthContext";
import { ErrorMessage, LiveMessage, Loading } from "../components/ui";
import { t } from "../i18n/t";
import { formatDate, roleLabel } from "../lib/format";
import { usePageTitle } from "../lib/usePageTitle";
import { useQuery } from "../lib/useQuery";
import { NotFoundPage } from "./NotFoundPage";

function MemberRow({
  orgId,
  member,
  isOwner,
  onChanged,
  onMessage,
}: {
  orgId: string;
  member: MemberOut;
  isOwner: boolean;
  onChanged: (self: boolean) => void;
  onMessage: (msg: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const name = member.name || member.email;

  async function changeRole(role: Role) {
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.PATCH("/api/orgs/{org_id}/members/{user_id}", {
          params: { path: { org_id: orgId, user_id: member.user_id } },
          body: { role },
        }),
      );
      onMessage(t("members.roleChanged", { name, role: roleLabel(role).toLowerCase() }));
      onChanged(member.is_me);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.DELETE("/api/orgs/{org_id}/members/{user_id}", {
          params: { path: { org_id: orgId, user_id: member.user_id } },
        }),
      );
      onMessage(t("members.removed", { name }));
      onChanged(member.is_me);
    } catch (err) {
      setError(toApiError(err));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <th scope="row">
        {member.name || t("common.dash")}
        {member.is_me && <span className="you-marker"> ({t("members.you")})</span>}
      </th>
      <td>{member.email}</td>
      <td>
        {isOwner ? (
          <select
            value={member.role}
            disabled={busy}
            aria-label={t("members.roleFor", { name })}
            onChange={(e) => void changeRole(e.target.value as Role)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        ) : (
          roleLabel(member.role)
        )}
      </td>
      {isOwner && (
        <td>
          {!member.is_me &&
            (confirming ? (
              <span className="link-group">
                <button
                  type="button"
                  className="btn btn-danger btn-small"
                  disabled={busy}
                  onClick={() => void remove()}
                  aria-label={t("members.confirmRemoveLabel", { name })}
                >
                  {t("common.confirmRemove")}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => setConfirming(false)}
                >
                  {t("common.cancel")}
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={() => setConfirming(true)}
                aria-label={t("members.removeLabel", { name })}
              >
                {t("common.remove")}
              </button>
            ))}
          {error && (
            <p className="field-error" role="alert">
              {error.userMessage}
            </p>
          )}
        </td>
      )}
    </tr>
  );
}

function InviteForm({ orgId, onInvited }: { orgId: string; onInvited: (email: string) => void }) {
  const id = useId();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const inv = await unwrap(
        api.POST("/api/orgs/{org_id}/invitations", {
          params: { path: { org_id: orgId } },
          body: { email: email.trim(), role },
        }),
      );
      setEmail("");
      setRole("member");
      onInvited(inv.email);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card form-inline" onSubmit={(e) => void onSubmit(e)}>
      <h2>{t("members.inviteTitle")}</h2>
      <div className="form-row">
        <div className="field">
          <label htmlFor={`${id}-email`}>{t("members.inviteEmail")}</label>
          <input
            id={`${id}-email`}
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-role`}>{t("members.inviteRole")}</label>
          <select id={`${id}-role`} value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-action">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t("common.working") : t("members.inviteSubmit")}
          </button>
        </div>
      </div>
      {error && <ErrorMessage error={error} />}
    </form>
  );
}

function PendingInvitations({
  orgId,
  reloadKey,
  onMessage,
}: {
  orgId: string;
  reloadKey: number;
  onMessage: (msg: string) => void;
}) {
  const list = useQuery(`invitations:${orgId}:${reloadKey}`, (signal) =>
    unwrap(
      api.GET("/api/orgs/{org_id}/invitations", {
        params: { path: { org_id: orgId } },
        signal,
      }),
    ),
  );
  const [error, setError] = useState<ApiError | null>(null);

  async function revoke(id: string, email: string) {
    setError(null);
    try {
      await unwrap(
        api.DELETE("/api/orgs/{org_id}/invitations/{invitation_id}", {
          params: { path: { org_id: orgId, invitation_id: id } },
        }),
      );
      list.setData((prev) => prev?.filter((i) => i.id !== id));
      onMessage(t("members.revoked", { email }));
    } catch (err) {
      setError(toApiError(err));
    }
  }

  return (
    <section className="section" aria-labelledby="pending-heading">
      <h2 id="pending-heading">{t("members.pendingTitle")}</h2>
      {list.error && <ErrorMessage error={list.error} onRetry={list.reload} />}
      {error && <ErrorMessage error={error} />}
      {!list.data && !list.error && <Loading />}
      {list.data && list.data.length === 0 && <p>{t("members.pendingEmpty")}</p>}
      {list.data && list.data.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <caption className="visually-hidden">{t("members.pendingCaption")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("members.colEmail")}</th>
                <th scope="col">{t("members.colRole")}</th>
                <th scope="col">{t("members.colSent")}</th>
                <th scope="col">{t("members.colExpires")}</th>
                <th scope="col">{t("members.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((inv) => (
                <tr key={inv.id}>
                  <th scope="row">{inv.email}</th>
                  <td>{roleLabel(inv.role)}</td>
                  <td>{formatDate(inv.created_at)}</td>
                  <td>{formatDate(inv.expires_at)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={() => void revoke(inv.id, inv.email)}
                      aria-label={t("members.revokeLabel", { email: inv.email })}
                    >
                      {t("members.revoke")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function MembersPage() {
  const { orgId = "" } = useParams();
  const me = useMe();
  const { refresh } = useAuth();
  const org = me.orgs.find((o) => o.org_id === orgId);
  const [message, setMessage] = useState("");
  const [invitesVersion, setInvitesVersion] = useState(0);
  usePageTitle(t("members.title"));

  const members = useQuery(org ? `members:${orgId}` : null, (signal) =>
    unwrap(api.GET("/api/orgs/{org_id}/members", { params: { path: { org_id: orgId } }, signal })),
  );

  if (!org) return <NotFoundPage />;
  const isOwner = org.role === "owner";

  return (
    <div className="page">
      <header className="page-header">
        <h1>{t("members.title")}</h1>
        <p className="muted">{t("members.intro", { org: org.org_name })}</p>
      </header>
      <LiveMessage message={message} />
      {members.error && <ErrorMessage error={members.error} onRetry={members.reload} />}
      {!members.data && !members.error && <Loading />}
      {members.data && (
        <div className="table-wrap">
          <table className="data-table">
            <caption className="visually-hidden">{t("members.caption")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("members.colName")}</th>
                <th scope="col">{t("members.colEmail")}</th>
                <th scope="col">{t("members.colRole")}</th>
                {isOwner && <th scope="col">{t("members.colActions")}</th>}
              </tr>
            </thead>
            <tbody>
              {members.data.map((m) => (
                <MemberRow
                  key={m.user_id}
                  orgId={orgId}
                  member={m}
                  isOwner={isOwner}
                  onChanged={(self) => {
                    members.reload();
                    // Own role changed: the session's org list (and owner rights) must follow.
                    if (self) void refresh();
                  }}
                  onMessage={setMessage}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isOwner ? (
        <>
          <InviteForm
            orgId={orgId}
            onInvited={(email) => {
              setMessage(t("members.inviteSent", { email }));
              setInvitesVersion((v) => v + 1);
            }}
          />
          <PendingInvitations orgId={orgId} reloadKey={invitesVersion} onMessage={setMessage} />
        </>
      ) : (
        <p className="muted">{t("members.onlyOwners")}</p>
      )}
    </div>
  );
}
